import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Menu, X, Phone } from 'lucide-react';
import { mockData } from '../data/mock';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Accueil', href: '/' },
    { name: 'À propos', href: '/a-propos' },
    { name: 'Nos services', href: '/services' },
    { name: 'Blog', href: '/blog' },
    { name: 'Actualités', href: '/actualites' },
    { name: 'Contact', href: '/contact' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 bg-white transition-all duration-300 ${
      scrolled ? 'header-scrolled' : ''
    }`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="/mmd-logo.png" 
              alt="MMD CONSEIL - Cabinet Expert-Comptable Lyon Rillieux-la-Pape" 
              className="h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center space-x-6">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`font-medium whitespace-nowrap transition-colors duration-200 hover:text-accent-blue ${
                  isActive(item.href)
                    ? 'text-accent-blue border-b-2 border-accent-blue pb-1'
                    : 'text-text-gray'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Access Buttons & Mobile Menu */}
          <div className="flex items-center space-x-3">
            {/* LinkedIn Button */}
            <Button
              asChild
              className="hidden 2xl:flex bg-[#0077B5] hover:bg-[#005885] text-white px-4 py-2 rounded-lg font-medium"
            >
              <a 
                href="https://www.linkedin.com/company/mmd-conseil" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center space-x-2"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                <span>LinkedIn</span>
              </a>
            </Button>

            {/* Pennylane Access Button */}
            <Button
              asChild
              variant="outline"
              className="hidden xl:flex btn-secondary px-4 py-2 rounded-lg font-medium text-sm"
            >
              <a 
                href="https://app.pennylane.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center space-x-2"
              >
                <img src="/pennylane-icon.png" alt="Pennylane" className="w-4 h-4" />
                <span>Pennylane</span>
              </a>
            </Button>

            <Button
              asChild
              className="hidden sm:flex btn-primary px-6 py-2 rounded-lg font-medium"
            >
              <a href="tel:+33482530684" className="flex items-center space-x-2">
                <Phone className="w-4 h-4" />
                <span>04 82 53 06 84</span>
              </a>
            </Button>

            {/* Bouton Appeler (mobile) */}
            <Button asChild size="sm" className="sm:hidden btn-primary px-3 py-2 rounded-lg font-medium">
              <a href="tel:+33482530684" data-testid="header-call-button-mobile" className="flex items-center space-x-1">
                <Phone className="w-4 h-4" />
                <span>Appeler</span>
              </a>
            </Button>

            {/* Mobile Menu */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="xl:hidden">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <div className="flex flex-col space-y-6 mt-8">
                  <div className="flex items-center space-x-3">
                    <img 
                      src="/mmd-logo.png" 
                      alt="MMD CONSEIL Logo" 
                      className="h-10 w-auto object-contain"
                    />
                  </div>

                  <nav className="flex flex-col space-y-4">
                    {navigation.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setIsOpen(false)}
                        className={`text-lg font-medium transition-colors duration-200 hover:text-accent-blue ${
                          isActive(item.href)
                            ? 'text-accent-blue'
                            : 'text-text-gray'
                        }`}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </nav>

                  <div className="pt-6 border-t space-y-3">
                    {/* LinkedIn */}
                    <Button
                      asChild
                      className="w-full bg-[#0077B5] hover:bg-[#005885] text-white py-3 rounded-lg font-medium"
                    >
                      <a 
                        href="https://www.linkedin.com/company/mmd-conseil" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        onClick={() => setIsOpen(false)}
                        className="flex items-center justify-center space-x-2"
                      >
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                        <span>LinkedIn</span>
                      </a>
                    </Button>

                    {/* Pennylane Access */}
                    <Button
                      asChild
                      variant="outline"
                      className="w-full btn-secondary py-3 rounded-lg font-medium"
                    >
                      <a 
                        href="https://app.pennylane.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        onClick={() => setIsOpen(false)}
                        className="flex items-center justify-center space-x-2"
                      >
                        <img src="/pennylane-icon.png" alt="Pennylane" className="w-5 h-5" />
                        <span>Accéder à Pennylane</span>
                      </a>
                    </Button>
                    
                    <Button
                      asChild
                      className="w-full btn-primary py-3 rounded-lg font-medium"
                    >
                      <a href="tel:+33482530684" onClick={() => setIsOpen(false)} className="flex items-center justify-center space-x-2">
                        <Phone className="w-4 h-4" />
                        <span>Appeler 04 82 53 06 84</span>
                      </a>
                    </Button>
                  </div>

                  <div className="text-sm text-text-gray text-center">
                    <p>{mockData.cabinet.telephone}</p>
                    <p>{mockData.cabinet.email}</p>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;